import React, { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { MODULE_KEYS } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import PlanningTableData from '../json/PlanningTable.json';
import {
  BaseRateCellRenderer,
  COMPA_RATIO_CELL_RENDERER,
  ChurnLikelihoodCellRenderer,
  EffectiveDateCellRenderer,
  EmployeeCellRenderer,
  LastDateOfChangeCellRenderer,
  NotesCellRenderer,
  ProposedBaseSalaryCellRenderer,
  RatingCellRenderer,
  RecommendationCellRenderer,
  WaitingOnCellRenderer,
} from './cellRenderers/CellRenderers';

type Props = {
  gridRef: React.RefObject<AgGridReact>;
};

const COLUMN_CELL_RENDERERS = {
  EMPLOYEE: EmployeeCellRenderer,
  RATING: RatingCellRenderer,
  CHURN_LIKELIHOOD: ChurnLikelihoodCellRenderer,
  WAITING_ON: WaitingOnCellRenderer,
  COMPA_RATIO: COMPA_RATIO_CELL_RENDERER,
  BASE_RATE: BaseRateCellRenderer,
  RECOMMENDATION: RecommendationCellRenderer,
  PROPOSED_BASE_SALARY: ProposedBaseSalaryCellRenderer,
  EFFECTIVE_DATE: EffectiveDateCellRenderer,
  LAST_DATE_OF_CHANGE: LastDateOfChangeCellRenderer,
  NOTES: NotesCellRenderer,
};

const PlanningTable = ({ gridRef }: Props) => {
  const cohortDetails = useSelectedView();
  const viewId = cohortDetails?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const uniqueId = 'MyCompanyCompensationPlanningGrid';

    return getPersistenceKey({
      moduleName: MODULE_KEYS.COMPENSATION,
      viewId,
      tabId: '',
      uniqueId,
    });
  }, [viewId]);

  const { columns, rows } = PlanningTableData;

  // eslint-disable-next-line arrow-body-style
  const columnDefs = useMemo<ColDef[]>(() => {
    return columns.map((col) => ({
      colId: col.columnId,
      headerComponent: CustomHeaderCell,
      sortable: true,
      field: col.columnType,
      headerName: col.displayName,
      cellRenderer: COLUMN_CELL_RENDERERS[col.columnType] || ((params) => <Box>{params.value}</Box>),
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      autoHeaderHeight: true,
      wrapHeaderText: true,
    }));
  }, [columns]);

  const popupParent = useMemo(() => document.querySelector('body'), []);

  return (
    <PaperWithShadow p="12px" mt={3}>
      <Box>
        <AgGridTable
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rows}
          domLayout="normal"
          pagination
          paginationPageSize={10}
          headerHeight={36}
          kebabMenuIsRowSpecific={false}
          rowHeight={72}
          suppressAggFuncInHeader
          popupParent={popupParent}
          // autoGroupColumnDef={autoGroupColumnDef}
          // defaultColDef={defaultColDef}
          // onFirstDataRendered={onFirstDataRendered}
          // onCellValueChanged={onCellValueChanged}
        />
      </Box>
    </PaperWithShadow>
  );
};

export { PlanningTable };
